import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import notify from 'Common/utils/notify';
import { toast } from 'react-toastify';
import styled from 'styled-components/macro';
import createGroupInstructionAssignment from 'Education/api/instruction-assignment/group/createGroupInstructionAssignment';
import getInstructions from 'Education/api/instruction/getInstructions';
import getEmployees from 'Employee/api/getEmployees';
import { useFormik } from 'formik';
import FormField from 'Common/components/Form/FormField';
import Select from 'Common/components/Form/Fields/Select';
import * as Yup from 'yup';
import axios from 'axios';
import useAxiosRequest from 'Common/hooks/useAxiosRequest';
import RetryLoading from 'Common/components/RetryLoading';
import Modal from 'Common/components/Modals/Modal';
import Button from 'Common/components/Button';
import ModalTitle from 'Common/components/Modals/ModalTitle';
import colors from 'Common/constants/colors';
import { orderBy } from 'lodash';

const SelectSecondaryLabel = styled.span`
    color: ${colors.LIGHT_GRAY};
`;

const validationSchema = Yup.object().shape({
    instruction: Yup.object().required('Pflichtangabe').nullable(),
    groupLeader: Yup.object().required('Pflichtangabe').nullable(),
    employees: Yup.array()
        .of(Yup.object())
        .required('Pflichtangabe')
        .nullable(),
});

const GroupInstructionAssignmentCreateModal = ({
    isOpen,
    onCancel,
    onSuccess,
}) => {
    const [isCreating, setIsCreating] = useState(false);

    const instructionsRequest = useCallback(
        cancelToken => getInstructions(['areas'], cancelToken),
        [],
    );

    const {
        data: instructions,
        loadData: loadInstructions,
        isLoading: isLoadingInstructions,
        hasError: hasInstructionsError,
    } = useAxiosRequest(instructionsRequest, []);

    const employeesRequest = useCallback(
        cancelToken =>
            getEmployees(
                ['areas', 'completedInstructions', 'assignedGroupInstructions'],
                cancelToken,
            ),
        [],
    );

    const {
        data: employees,
        loadData: loadEmployees,
        isLoading: isLoadingEmployees,
        hasError: hasEmployeesError,
    } = useAxiosRequest(employeesRequest, []);

    const isLoading = isLoadingInstructions || isLoadingEmployees;
    const hasError = hasInstructionsError || hasEmployeesError;

    const handleRetryLoading = () => {
        loadInstructions();
        loadEmployees();
    };

    const source = useMemo(() => axios.CancelToken.source(), []);

    useEffect(
        () => () => {
            source.cancel();
        },
        [source],
    );

    const {
        errors,
        touched,
        values,
        setFieldValue,
        setValues,
        handleBlur,
        handleSubmit,
        isValid,
    } = useFormik({
        initialValues: {
            instruction: null,
            groupLeader: null,
            employees: [],
        },
        validationSchema,
        onSubmit: async values => {
            setIsCreating(true);

            try {
                const response = await createGroupInstructionAssignment(
                    {
                        instructionId: values.instruction.value,
                        groupLeaderId: values.groupLeader.value,
                        employeeIds: values.employees.map(
                            option => option.value,
                        ),
                    },
                    source.token,
                );

                await onSuccess(response.data);

                setIsCreating(false);

                notify('Der Präsenzunterweisung wurde erfolgreich erstellt', {
                    type: toast.TYPE.SUCCESS,
                });
            } catch (error) {
                if (!axios.isCancel(error)) {
                    setIsCreating(false);

                    notify(
                        'Der Präsenzunterweisung wurde nicht erfolgreich erstellt',
                        {
                            type: toast.TYPE.ERROR,
                        },
                    );
                }
            }
        },
    });

    const instructionId = values.instruction ? values.instruction.value : null;
    const groupLeaderId = values.groupLeader ? values.groupLeader.value : null;
    const selectedEmployeeOptions = values.employees ?? [];

    const instructionOptions = instructions.map(instruction => ({
        value: instruction.id,
        label: instruction.name,
        instruction,
    }));

    const employeesWithMatchingAreas = orderBy(
        employees.filter(employee => {
            if (! employee.isEnabled) {
                return false;
            }

            const instruction = instructions.find(
                instruction => instruction.id === instructionId,
            );

            if (!instruction) {
                return false;
            }

            const instructionAreaIds = instruction.areas.map(area => area.id);
            const employeeAreaIds = employee.areas.map(area => area.id);
            const intersectingAreaIds = instructionAreaIds.filter(areaId =>
                employeeAreaIds.includes(areaId),
            );

            return intersectingAreaIds.length > 0;
        }),
        ['lastName', 'firstName'],
        ['asc', 'asc'],
    );

    const leaderEmployeeOptions = employeesWithMatchingAreas
        .filter(employee => !employee.isTrainee)
        .map(employee => ({
            value: employee.id,
            label: `${employee.lastName}, ${employee.firstName}`,
        }));

    const employeeOptions = employeesWithMatchingAreas
        .filter(employee => {
            const groupInstructionIds = employee.assignedGroupInstructions.map(
                instruction => instruction.id,
            );
            const completedInstructionIds = employee.completedInstructions.map(
                instruction => instruction.id,
            );

            const isPartOfAnotherGroupAssignmentOfThisInstruction = groupInstructionIds.includes(
                instructionId,
            );

            const hasCompletedInstruction = completedInstructionIds.includes(
                instructionId,
            );

            return (
                !isPartOfAnotherGroupAssignmentOfThisInstruction &&
                !hasCompletedInstruction
            );
        })
        .map(employee => ({
            value: employee.id,
            label: `${employee.lastName}, ${employee.firstName}`,
        }));

    const handleInstructionChange = option => {
        setValues({
            ...values,
            instruction: option,
            groupLeader: null,
            employees: [],
        });
    };

    const handleGroupLeaderChange = option => {
        const newEmployees = [...selectedEmployeeOptions];

        // Remove previously selected option
        if (groupLeaderId) {
            const currentlySelectedOptionIndex = selectedEmployeeOptions.findIndex(
                selectedOption => selectedOption.value === groupLeaderId,
            );

            if (currentlySelectedOptionIndex !== -1) {
                newEmployees.splice(currentlySelectedOptionIndex, 1);
            }
        }

        // Add new option
        if (option) {
            const optionIndex = employeeOptions.findIndex(
                employeeOption => employeeOption.value === option.value,
            );

            const isAlreadySelected =
                selectedEmployeeOptions.findIndex(
                    selectedOption => selectedOption.value === option.value,
                ) !== -1;

            if (optionIndex !== -1 && !isAlreadySelected) {
                newEmployees.push(option);
            }
        }

        setValues({
            ...values,
            groupLeader: option,
            employees: newEmployees,
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            isLoading={isLoading}
            onRequestClose={isCreating ? undefined : onCancel}
            buttons={
                !hasError && (
                    <>
                        <Button
                            text
                            dark
                            type="button"
                            onClick={onCancel}
                            disabled={isCreating}
                        >
                            Abbrechen
                        </Button>
                        <Button
                            type="button"
                            isLoading={isCreating}
                            onClick={handleSubmit}
                            disabled={isCreating || !isValid}
                        >
                            Erstellen
                        </Button>
                    </>
                )
            }
        >
            {!isLoading &&
                (hasError ? (
                    <RetryLoading onRetry={handleRetryLoading} />
                ) : (
                    <>
                        <ModalTitle>Präsenzunterweisung erstellen</ModalTitle>
                        <form onSubmit={handleSubmit}>
                            <FormField>
                                <Select
                                    label="Unterweisung"
                                    id="instruction"
                                    name="instruction"
                                    error={errors.instruction}
                                    touched={touched.instruction}
                                    value={values.instruction}
                                    onChange={handleInstructionChange}
                                    onBlur={handleBlur}
                                    options={instructionOptions}
                                    menuPosition="fixed"
                                    menuPortalTarget={document.body}
                                    formatOptionLabel={({
                                        label,
                                        instruction,
                                    }) => {
                                        const areas = instruction.areas
                                            .map(area => area.name)
                                            .join(', ');

                                        return (
                                            <>
                                                {label}{' '}
                                                <SelectSecondaryLabel>
                                                    ({areas})
                                                </SelectSecondaryLabel>
                                            </>
                                        );
                                    }}
                                    isSearchable
                                    isClearable
                                />
                            </FormField>
                            <FormField>
                                <Select
                                    label="Gruppenleiter"
                                    id="groupLeader"
                                    name="groupLeader"
                                    error={errors.groupLeader}
                                    touched={touched.groupLeader}
                                    value={values.groupLeader}
                                    onChange={handleGroupLeaderChange}
                                    onBlur={handleBlur}
                                    options={leaderEmployeeOptions}
                                    menuPosition="fixed"
                                    menuPortalTarget={document.body}
                                    isSearchable
                                    isClearable
                                />
                            </FormField>
                            <FormField>
                                <Select
                                    label="Mitarbeiter"
                                    id="employees"
                                    name="employees"
                                    error={errors.employees}
                                    touched={touched.employees}
                                    value={values.employees}
                                    setFieldValue={setFieldValue}
                                    onBlur={handleBlur}
                                    options={employeeOptions}
                                    menuPosition="fixed"
                                    menuPortalTarget={document.body}
                                    isMulti
                                    isSearchable
                                    isClearable
                                />
                            </FormField>
                        </form>
                    </>
                ))}
        </Modal>
    );
};

GroupInstructionAssignmentCreateModal.propTypes = {
    isOpen: PropTypes.bool,
    onCancel: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
};

export default GroupInstructionAssignmentCreateModal;
